import translate from '@allesauto/i18n';
import { SupportedCurrencies, ViewModes } from '@allesauto/utils/consts';
import { formatPrice } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import TextLink from '../textLink';

export interface PriceProps {
  price: number | null;
  priceTextKey?: string;
  monthlyPrice?: number;
  monthlyTextKey?: string;
  displayView?: ViewModes;
  currency?: SupportedCurrencies;
  isMonthlyMainPrice?: boolean;
  classNames?: {
    wrapperClass?: string;
    mainPriceClass?: string;
    monthlyPriceWrapperClass?: string;
    monthlyPriceTextClass?: string;
    monthlyPriceClass?: string;
  };
  hasFractionDigits?: boolean;
  hasMonthlyFactionDigits?: boolean;
  priceSupText?: string;
  isTeaserPrice?: boolean;
  mwstPcp?: boolean | null | undefined;
  financingDetailsLinkPcp?: boolean | undefined;
  financeTypeText?: string;
  refTextlink?: React.RefObject<HTMLDivElement>;
  handleFinancingDetailsClick?: (e: React.MouseEvent) => Promise<void>;
}

const Price: FC<PriceProps> = (props: PriceProps): JSX.Element => {
  const {
    price,
    priceTextKey = 'ui.PRICE.TEXT',
    monthlyPrice,
    monthlyTextKey = 'ui.PRICE.MONTHLY_TEXT',
    displayView,
    isMonthlyMainPrice = false,
    classNames,
    hasFractionDigits = false,
    hasMonthlyFactionDigits = true,
    priceSupText,
    isTeaserPrice = false,
    mwstPcp = false,
    financingDetailsLinkPcp = false,
    financeTypeText,
    refTextlink,
    handleFinancingDetailsClick,
  } = props;

  const intl = useIntl();

  const className = {
    wrapperClass: clsx(
      'flex flex-col',
      displayView === ViewModes.GRID ? 'text-left' : 'text-right',
      classNames?.wrapperClass
    ),
    mainPriceClass: clsx(
      isMonthlyMainPrice
        ? 'order-1 text-lightPurple monochrome:text-grey60 text-p2'
        : 'order-none text-purple80 monochrome:text-black text-headline3',
      classNames?.mainPriceClass
    ),
    teaserTextClass: 'font-regular mr-5px text-legal',
    monthlyPriceWrapperClass: clsx(
      'flex items-center',
      displayView === ViewModes.GRID ? 'justify-start' : 'justify-end',
      classNames?.monthlyPriceWrapperClass
    ),
    monthlyPriceTextClass: clsx(
      isMonthlyMainPrice
        ? 'items-center  pr-5px pt-3px text-[10px] leading-[10px] text-purple80 monochrome:text-black text-right font-regular'
        : 'text-lightPurple monochrome:text-grey60 text-p2 pr-5px',
      classNames?.monthlyPriceTextClass
    ),
    monthlyPriceClass: clsx(
      isMonthlyMainPrice
        ? 'text-purple80 monochrome:text-black text-headline3'
        : 'text-lightPurple monochrome:text-grey60 text-p2',
      classNames?.monthlyPriceClass
    ),
  };

  const formattedPrice = translate(intl, priceTextKey, {
    price: formatPrice(intl, price, {
      withFractionDigits: hasFractionDigits,
    }),
  });

  const splitPrice = formattedPrice.split(' ');

  return (
    <div className={className.wrapperClass}>
      <div className={'font-headline font-weight-bold'}>
        {price !== null && priceTextKey && (
          <div className={className.mainPriceClass}>
            {splitPrice.length === 1 ? (
              <span>{formattedPrice}</span>
            ) : (
              <>
                {splitPrice.map((priceText, index) => (
                  <span
                    className={
                      isTeaserPrice && index === 0
                        ? className.teaserTextClass
                        : ''
                    }
                    key={priceText + index}
                  >
                    {priceText}
                    {index !== splitPrice.length - 1 && ' '}
                  </span>
                ))}
              </>
            )}
          </div>
        )}
        {!!monthlyPrice && !!monthlyTextKey && (
          <div className={className.monthlyPriceWrapperClass}>
            <span className={className.monthlyPriceTextClass}>
              {translate(intl, monthlyTextKey)}
            </span>
            <span className={className.monthlyPriceClass}>
              {formatPrice(intl, monthlyPrice, {
                withFractionDigits: hasMonthlyFactionDigits,
              })}
            </span>
            {priceSupText && <sup>{priceSupText}</sup>}
          </div>
        )}
      </div>
      <div>
        {financingDetailsLinkPcp && (
          <div ref={refTextlink} data-finance>
            <TextLink
              text={translate(intl, 'ui.FINANCE_DETAILS.FINANCE_DETAILS_TEXT', {
                financeType: financeTypeText as string,
              })}
              icon={null}
              textColor="text-lightPurple monochrome:text-grey60"
              fillColor="fill-lightPurple monochrome:fill-grey60"
              customClassName="relative z-[5]"
              onClick={handleFinancingDetailsClick}
            />
          </div>
        )}
        {mwstPcp && (
          <p
            className={
              financingDetailsLinkPcp
                ? 'text-black text-p3 break-words inline-block mt-[15px]'
                : 'text-black text-p3 break-words mt-[8px]'
            }
          >
            {translate(intl, 'ui.PCP.FILTERS.VEHICLE_LEGAL_TEXT')}
          </p>
        )}
      </div>
    </div>
  );
};

export default Price;
